import React, { useContext, useState } from 'react';
import { Alert, ProgressBar } from 'react-bootstrap';
import { LocalizeContext } from 'react-locale-language';
import { Link, Redirect, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { UserContext } from '../../context/UserContext';
import { makeApiRequests } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import { validateEmail } from '../../helpers/global';
import LanguagePicker from '../common/LanguagePicker';
import { BrandAside, LabeledInputField, authSubmitButtonClass, authLinkClass } from '../common/authUi';

const formStackClass = 'flex flex-col gap-3 md:gap-5 lg:gap-6';

/** Same keys for field values and field-level validation messages (phone optional, no validation). */
const emptyForm = { firstName: '', lastName: '', email: '', phone: '' };

const GetStarted = () => {
  const [values, setValues] = useState(emptyForm);
  const [errors, setErrors] = useState(emptyForm);
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { translate } = useContext(LocalizeContext);
  const { isUserLoggedIn } = useContext(UserContext);
  const history = useHistory();

  if (isUserLoggedIn) {
    return <Redirect to="/" />;
  }

  const handleChange = (key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors(prev => ({ ...prev, [key]: '' }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setApiError('');
    const newErrors = {
      firstName: !values.firstName.trim() ? translate('field_required') : '',
      lastName: !values.lastName.trim() ? translate('field_required') : '',
      email: !values.email.trim() || !validateEmail(values.email) ? translate('invalid_email_short') : '',
      phone: ''
    };

    if (Object.values(newErrors).some(Boolean)) {
      setErrors(newErrors);
      return;
    }

    setSubmitting(true);
    try {
      const { response, error } = await makeApiRequests({
        endpoint: ENDPOINTS.GET_STARTED,
        requestBody: {
          name: `${values.firstName.trim()} ${values.lastName.trim()}`,
          email: values.email.trim(),
          phone: values.phone.trim()
        },
        method: 'POST'
      });

      if (error) {
        setApiError(error);
        return;
      }
      if (response) {
        toast.success(
          'A link to create your password has been sent to your email. Please check your mail box',
          { autoClose: 5000 }
        );
      }
      history.push('/login');
    } catch (err) {
      setApiError('Something went wrong! Please try again');
      console.log(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-dvh w-full flex-col overflow-x-hidden bg-brand-surface font-sans text-brand-navy lg:min-h-screen lg:flex-row lg:overflow-hidden">
      <BrandAside titleKey="get_started_brand_headline" taglineKey="get_started_brand_tagline" />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col lg:min-h-screen">
        <header className="flex w-full flex-wrap items-center justify-end gap-x-3 gap-y-3 border-b border-brand-navy/6 bg-brand-surface px-4 py-3.5 sm:gap-x-5 sm:px-[clamp(1.25rem,4vw,3rem)] sm:py-5">
          <div className="flex flex-shrink-0 items-center gap-4 sm:gap-6">
            <LanguagePicker />
          </div>
        </header>

        <main className="flex min-h-0 flex-1 flex-col items-stretch justify-start px-4 py-6 sm:justify-center sm:px-[clamp(1.25rem,5vw,3rem)] sm:py-10 md:py-12 md:pb-16">
          <div className="w-full max-w-auth-form self-center">
            <p className="mb-2 text-sm text-gray-500 sm:mb-3">{translate('welcome_to_ETICA_financials')}</p>
            <h1 className="mb-8 font-serif text-3xl font-semibold leading-tight tracking-tight sm:mb-10 sm:text-4xl">
              {translate('please_fill_the_form_to_continue')}
            </h1>

            <form className={formStackClass} onSubmit={handleSubmit} noValidate>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-x-3 md:gap-5">
                <LabeledInputField
                  id="gs-first-name"
                  label={translate('first_name')}
                  type="text"
                  autoComplete="given-name"
                  value={values.firstName}
                  error={errors.firstName}
                  onValueChange={v => handleChange('firstName', v)}
                  disabled={submitting}
                />
                <LabeledInputField
                  id="gs-last-name"
                  label={translate('last_name')}
                  type="text"
                  autoComplete="family-name"
                  value={values.lastName}
                  error={errors.lastName}
                  onValueChange={v => handleChange('lastName', v)}
                  disabled={submitting}
                />
              </div>
              <LabeledInputField
                id="gs-email"
                label={translate('email_get_started')}
                type="email"
                autoComplete="email"
                value={values.email}
                error={errors.email}
                onValueChange={v => handleChange('email', v)}
                disabled={submitting}
              />
              <LabeledInputField
                id="gs-phone"
                label={translate('phone_number')}
                type="tel"
                autoComplete="tel"
                value={values.phone}
                error={errors.phone}
                onValueChange={v => handleChange('phone', v)}
                disabled={submitting}
              />
              <button type="submit" className={authSubmitButtonClass} disabled={submitting}>
                {translate('register')}
              </button>
            </form>

            {submitting && (
              <ProgressBar
                className="mt-5"
                striped
                animated
                variant="dark"
                now={100}
                label={`${translate('creating_user')}...`}
              />
            )}
            {apiError && (
              <Alert className="mt-4 text-sm" variant="danger">
                {apiError}
              </Alert>
            )}

            <p className="mt-8 text-center text-sm sm:mt-10">
              <Link
                to="/login"
                className={`${authLinkClass} ${submitting ? 'pointer-events-none text-muted' : ''}`}
              >
                {translate('already_have_an_account')}
              </Link>
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default GetStarted;
